"use client";
import React, { useRef, useState, useEffect } from "react";
import Link from "next/link";
import { faChevronLeft, faChevronRight, FontAwesomeIcon } from "@/constants/icons";

interface Category {
    id: number;
    name: string;
    slug: string;
}

interface Props {
    categories: Category[];
    merchantSlug: string;
}

export default function CategoryPillSlider({ categories, merchantSlug }: Props) {
    const ref = useRef<HTMLDivElement>(null);
    const [canLeft, setCanLeft]   = useState(false);
    const [canRight, setCanRight] = useState(false);

    const update = () => {
        const el = ref.current;
        if (!el) return;
        setCanLeft(el.scrollLeft > 4);
        setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };

    useEffect(() => {
        update();
        const el = ref.current;
        el?.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        return () => {
            el?.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, []);

    const scroll = (dir: "left" | "right") => {
        ref.current?.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
    };

    const btnBase = "w-7 h-7 rounded-full border shadow-md flex items-center justify-center transition-all duration-200 shrink-0";
    const btnActive = "bg-white border-slate-200 text-slate-500 hover:bg-[#D61C4E] hover:border-[#D61C4E] hover:text-white cursor-pointer";
    const btnDisabled = "bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed";

    return (
        <div className="flex items-center gap-3">
            {/* Left button — always visible */}
            <button
                onClick={() => scroll("left")}
                disabled={!canLeft}
                className={`${btnBase} ${canLeft ? btnActive : btnDisabled}`}
                aria-label="Scroll left"
            >
                <FontAwesomeIcon icon={faChevronLeft} className="w-2.5 h-2.5" />
            </button>

            {/* Pills */}
            <div
                ref={ref}
                onScroll={update}
                className="flex gap-2 overflow-x-auto scroll-smooth flex-1
                           [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
                {categories.map((item, i) => (
                    <Link
                        key={item.id || i}
                        href={`/products/${merchantSlug}/${item.slug}`}
                        className="group shrink-0 px-4 py-2 rounded-xl
                                   border border-slate-100 bg-slate-50
                                   hover:bg-[#D61C4E] hover:border-[#D61C4E]
                                   transition-all duration-200 no-underline"
                    >
                        <span className="text-[11px] font-bold text-slate-600
                                         group-hover:text-white transition-colors
                                         uppercase tracking-tight whitespace-nowrap">
                            {item.name}
                        </span>
                    </Link>
                ))}
            </div>

            {/* Right button — always visible */}
            <button
                onClick={() => scroll("right")}
                disabled={!canRight}
                className={`${btnBase} ${canRight ? btnActive : btnDisabled}`}
                aria-label="Scroll right"
            >
                <FontAwesomeIcon icon={faChevronRight} className="w-2.5 h-2.5" />
            </button>
        </div>
    );
}
